import { type ChangeEvent, type FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCategories, useCreateProject } from "../lib/api";
import { type StoredFile, uploadToStorage } from "../lib/storage";
import { validateImage } from "../lib/validators";

interface Attached {
  localId: string;
  name: string;
  progress: number;
  stored?: StoredFile;
  error?: string;
}

export function NewProject() {
  const { data: categories } = useCategories();
  const create = useCreateProject();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [description, setDescription] = useState("");
  const [features, setFeatures] = useState("");
  const [files, setFiles] = useState<Attached[]>([]);
  const [thumb, setThumb] = useState<{ url: string; key: string } | null>(null);
  const [thumbErr, setThumbErr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function onThumbPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const err = validateImage(file);
    if (err) {
      setThumbErr(err);
      return;
    }
    setThumbErr(null);
    try {
      const stored = await uploadToStorage(file);
      setThumb({ url: URL.createObjectURL(file), key: stored.storagePath });
    } catch (uploadErr) {
      setThumbErr(uploadErr instanceof Error ? uploadErr.message : "이미지 업로드 실패");
    }
  }

  async function onPick(e: ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = "";
    for (const file of picked) {
      const localId = crypto.randomUUID();
      setFiles((prev) => [...prev, { localId, name: file.name, progress: 0 }]);
      try {
        const stored = await uploadToStorage(file, (pct) =>
          setFiles((prev) =>
            prev.map((f) => (f.localId === localId ? { ...f, progress: pct } : f)),
          ),
        );
        setFiles((prev) =>
          prev.map((f) => (f.localId === localId ? { ...f, stored, progress: 100 } : f)),
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : "업로드 실패";
        setFiles((prev) => prev.map((f) => (f.localId === localId ? { ...f, error: message } : f)));
      }
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (title.trim().length === 0) {
      setError("제목을 입력해주세요");
      return;
    }
    if (files.some((f) => !f.stored && !f.error)) {
      setError("파일 업로드가 끝날 때까지 기다려주세요");
      return;
    }
    setError(null);
    try {
      const stored = files.map((f) => f.stored).filter((s): s is StoredFile => Boolean(s));
      const id = await create.mutateAsync({
        title: title.trim(),
        description,
        features,
        categoryId,
        board: "public",
        thumbnailKey: thumb?.key ?? null,
        files: stored,
      });
      navigate(`/projects/${id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "게시에 실패했습니다");
    }
  }

  return (
    <section className="mx-auto max-w-2xl fade-up">
      <div className="glass neon-border rounded-2xl p-7">
        <h1 className="neon-text text-2xl font-bold">프로젝트 등록</h1>
        <form className="mt-5 flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          <label className="flex flex-col gap-1 text-sm text-slate-300">
            제목
            <input
              aria-label="제목"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="input-cyber rounded-lg px-3 py-2.5"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-slate-300">
            카테고리
            <select
              aria-label="카테고리"
              value={categoryId ?? ""}
              onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : null)}
              className="input-cyber rounded-lg px-3 py-2.5"
            >
              <option value="">선택 안 함</option>
              {categories?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm text-slate-300">
            소개
            <textarea
              aria-label="소개"
              value={description}
              rows={3}
              onChange={(e) => setDescription(e.target.value)}
              className="input-cyber rounded-lg px-3 py-2.5"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-slate-300">
            주요 기능
            <textarea
              aria-label="주요 기능"
              value={features}
              rows={5}
              onChange={(e) => setFeatures(e.target.value)}
              className="input-cyber rounded-lg px-3 py-2.5"
            />
          </label>

          <div className="flex flex-col gap-2">
            <label className="text-sm text-slate-300">
              대표 이미지 (선택)
              <input
                aria-label="대표 이미지"
                type="file"
                accept="image/*"
                onChange={onThumbPick}
                className="mt-1 block w-full text-sm text-slate-400 file:mr-3 file:rounded-lg file:border file:border-cyan-400/40 file:bg-cyan-400/10 file:px-3 file:py-1.5 file:text-cyan-200"
              />
            </label>
            {thumbErr && <p className="text-xs text-fuchsia-400">{thumbErr}</p>}
            {thumb && (
              <img
                src={thumb.url}
                alt="대표 이미지 미리보기"
                className="h-32 w-auto rounded-lg border border-cyan-400/20 object-cover"
              />
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm text-slate-300">
              첨부 파일 (최대 2GB)
              <input
                aria-label="파일"
                type="file"
                multiple
                onChange={onPick}
                className="mt-1 block w-full text-sm text-slate-400 file:mr-3 file:rounded-lg file:border file:border-cyan-400/40 file:bg-cyan-400/10 file:px-3 file:py-1.5 file:text-cyan-200"
              />
            </label>
            {files.map((f) => (
              <div key={f.localId} className="rounded-lg border border-slate-800 px-3 py-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-slate-200">{f.name}</span>
                  <span className="font-mono text-slate-500">
                    {f.error ? "실패" : f.stored ? "완료" : `${f.progress}%`}
                  </span>
                </div>
                {!f.stored && !f.error && (
                  <div className="mt-1 h-1 overflow-hidden rounded bg-slate-800">
                    <div
                      className="h-full bg-cyan-400 transition-all"
                      style={{ width: `${f.progress}%` }}
                    />
                  </div>
                )}
                {f.error && <p className="mt-1 text-fuchsia-400">{f.error}</p>}
              </div>
            ))}
          </div>

          {error && <p className="text-sm text-fuchsia-400">{error}</p>}
          <button
            type="submit"
            disabled={create.isPending}
            className="btn-neon rounded-lg py-2.5 font-semibold"
          >
            게시하기
          </button>
        </form>
      </div>
    </section>
  );
}
